/**
 * Base class for browser-side RPC methods.
 *
 * Provides tool registration and execution infrastructure
 * for browser capabilities that agents can invoke.
 */

import type { BrowserToolDefinition, BrowserToolGroup } from './types';

export type ToolHandler = (params: Record<string, unknown>) => Promise<unknown>;

/**
 * MCP-standard annotations (https://modelcontextprotocol.io/specification).
 *
 * Clients use these to make trust decisions (auto-approve read-only,
 * require confirmation for destructive, etc.). Always declare them — the
 * pessimistic defaults (non-readOnly, destructive, non-idempotent,
 * openWorld) should only be used when that's really what the tool does.
 */
export interface ToolAnnotations {
  /** Does this tool modify state? */
  readOnlyHint?: boolean;
  /** Is the modification irreversible? */
  destructiveHint?: boolean;
  /** Is it safe to retry with the same arguments? */
  idempotentHint?: boolean;
  /** Does it interact with external systems (non-local side effect)? */
  openWorldHint?: boolean;
}

export interface RegisteredTool {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
  handler: ToolHandler;
  /** Tool category; the server maps it to a disclosure group as fallback. */
  category?: string;
  /** Progressive-disclosure group used by the agent toolkit (spec 089). */
  disclosureGroup?: BrowserToolGroup;
  /** Agent slugs allowed to discover this tool. Omit for all agents. */
  visibleToAgents?: string[];
  requiresPermission?: string[];
  /** If true, this tool is fire-and-forget (no response expected) */
  fireAndForget?: boolean;
  /** MCP-standard annotations for client trust decisions (Principle 12). */
  annotations?: ToolAnnotations;
  /**
   * JSON Schema of the tool's return value (MCP ``outputSchema``).
   * Declared when predictable so clients can validate and agents can plan
   * without prose-parsing the description.
   */
  outputSchema?: Record<string, unknown>;
}

/**
 * Optional, per-frontend metadata describing the `browser` tool group as
 * a whole. Surfaces in the agent's tool-discovery catalog (description) and
 * in the activated group's notes — see specs/047-browser-tools-pd.
 */
export interface BrowserGroupMeta {
  description?: string;
  notes?: string;
}

/**
 * Base class for managing browser RPC methods.
 *
 * Provides:
 * - Tool registration
 * - Tool execution
 * - Tool definitions for discovery
 * - Optional group-level metadata (description / notes)
 */
export class BrowserRpcMethods {
  private tools = new Map<string, RegisteredTool>();
  private groupMeta: BrowserGroupMeta = {};

  /**
   * Register a browser tool.
   *
   * @param tool Tool definition with handler
   */
  registerTool(tool: RegisteredTool): void {
    this.tools.set(tool.name, tool);
  }

  /**
   * Update a browser tool's metadata in place (spec 048 FR-003).
   *
   * Implicit-add when the name is absent (Clarification Q1). The
   * existing ``unregisterTool`` and ``hasTool`` methods further down
   * were already in place before spec 048 and are reused unchanged.
   */
  updateTool(tool: RegisteredTool): void {
    this.tools.set(tool.name, tool);
  }

  /**
   * Execute a tool by name.
   *
   * @param name Tool name
   * @param params Tool parameters
   * @returns Tool result
   * @throws Error if tool not found
   */
  async executeTool(
    name: string,
    params: Record<string, unknown> = {},
  ): Promise<unknown> {
    const tool = this.tools.get(name);
    if (!tool) {
      throw {
        code: -32004,
        message: `Unknown tool: ${name}`,
      };
    }

    try {
      return await tool.handler(params);
    } catch (error) {
      // Re-throw RPC errors
      if (typeof error === 'object' && error !== null && 'code' in error) {
        throw error;
      }
      throw {
        code: -32603,
        message: String(error),
      };
    }
  }

  /**
   * Get tool definitions for agent discovery.
   *
   * @returns Array of tool definitions
   */
  getToolDefinitions(): BrowserToolDefinition[] {
    const definitions: BrowserToolDefinition[] = [];

    for (const tool of this.tools.values()) {
      definitions.push({
        name: tool.name,
        description: tool.description,
        parameters: tool.parameters,
        category: tool.category,
        disclosureGroup: tool.disclosureGroup,
        visibleToAgents: tool.visibleToAgents,
        requiresPermission: tool.requiresPermission,
        fireAndForget: tool.fireAndForget,
        annotations: tool.annotations,
        outputSchema: tool.outputSchema,
      });
    }

    return definitions;
  }

  /**
   * Unregister a browser tool.
   *
   * @param name Tool name to remove
   * @returns true if tool was removed, false if not found
   */
  unregisterTool(name: string): boolean {
    return this.tools.delete(name);
  }

  /**
   * Check if a tool is registered.
   *
   * @param name Tool name
   */
  hasTool(name: string): boolean {
    return this.tools.has(name);
  }

  /**
   * Get registered tool count.
   */
  get toolCount(): number {
    return this.tools.size;
  }

  /**
   * Set frontend-supplied group description / notes for the `browser` group.
   * Either field may be omitted; passing an empty string clears that field.
   * Backend trims, length-caps, and silently drops invalid values.
   */
  setGroupMeta(meta: BrowserGroupMeta): void {
    this.groupMeta = {
      ...(meta.description !== undefined ? { description: meta.description } : {}),
      ...(meta.notes !== undefined ? { notes: meta.notes } : {}),
    };
  }

  /**
   * Get the currently set group metadata (empty object when nothing is set).
   */
  getGroupMeta(): BrowserGroupMeta {
    return { ...this.groupMeta };
  }
}
